<template>
    <div class="map-filter">
        <label class="headline portal-headline">{{ translate(translations, 'location.radius') }}</label>
        <div class="radio-group mb-3">
            <div class="radio">
                <input id="10km" v-model="distance" type="radio" value="10" checked>
                <label for="10km">10 km</label>
            </div>
            <div class="radio">
                <input id="20km" v-model="distance" type="radio" value="20">
                <label for="20km">20 km</label>
            </div>
            <div class="radio">
                <input id="30km" v-model="distance" type="radio" value="30">
                <label for="30km">30 km</label>
            </div>
        </div>
        <div class="form-group">
            <div class="input-group">
                <input
                    id="map-zip-input" v-model="zip" type="text" :placeholder="translate(translations, 'location.zip')"
                    @keyup.enter="getFilteredLocations"
                >
                <button v-if="zip.length" class="rest-btn" type="button" @click="resetFilter">
                    x
                </button>
            </div>
            <button class="btn btn-primary filter-btn" @click="getFilteredLocations">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"/>
                <span v-else class="icon after i-settings">{{
                        translate(translations, 'location.filterlocations')
                    }}</span>
            </button>
        </div>
        <div class="error">
            <label v-if="errorMessage" for="map-zip-input" class="error-label">{{ errorMessage }}</label>
        </div>
        <div v-if="filteredLocations.length" class="result-list">
            <label class="headline">{{ translate(translations, 'location.searchresults') }}</label>
            <div v-for="location in filteredLocations" :key="location.uid">
                <CAccordion>
                    <CAccordionItem :item-key="location.uid">
                        <CAccordionHeader>
                            <div class="location-header-text">
                                <span class="header-location">{{ defaultLocations[location.uid].name }}</span>
                                <span class="header-address subheader">{{ defaultLocations[location.uid].address }}, {{ defaultLocations[location.uid].zip }} {{ defaultLocations[location.uid].city }}</span>
                                <span v-if="typeof location.distance === 'number'" class="location-header-distance subheader">{{
                                        Number(location.distance).toFixed(2)
                                    }} KM {{ translate(translations, 'location.away') }}</span>
                                <svg class="toggle-icon" viewBox="0 0 16 16">
                                    <path
                                        d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                                </svg>
                            </div>
                        </CAccordionHeader>
                        <CAccordionBody>
                            <location-accordion-body
                                :location="defaultLocations[location.uid]"
                                :translations="translations"
                            />
                        </CAccordionBody>
                    </CAccordionItem>
                </CAccordion>
            </div>
        </div>
    </div>
</template>

<script>
import {
	CAccordion,
	CAccordionBody,
	CAccordionHeader,
	CAccordionItem,
} from '@coreui/vue/';
import $ from 'jquery';
import { ref, toRaw, watchEffect } from 'vue';
import { translate } from '../translate';
import LocationAccordionBody from './LocationAccordionBody.vue';

export default {
	name: 'MapFilter',
	components: {
		LocationAccordionBody,
		CAccordion,
		CAccordionBody,
		CAccordionHeader,
		CAccordionItem,
	},
	props: {
		locations: {
			type: Object,
			default: () => ({}), // Set default value as an empty object
		},
		defaultZipSearchLocation: {
			type: String,
			default: '',
		},
		filterUrl: {
			type: String,
			required: true,
		},
		translations: {
			type: Object,
			required: true,
		},
	},

	setup(props) {
		const zip = ref('');
		const distance = ref(10);
		const filteredLocations = ref([]);
		const locationUids = Object.values(props.locations).map(
			(location) => location.uid,
		);
		const errorMessage = ref('');
		const filterActive = ref(false);
		const isLoading = ref(false);

		const defaultLocations = ref(
			Object.values(props.locations).reduce((acc, location) => {
				acc[location.uid] = location;
				return acc;
			}, {}),
		);

		const getFilteredLocations = () => {
			filterActive.value = true;
			isLoading.value = true;
			errorMessage.value = '';
			$.ajax({
				url: props.filterUrl,
				data: {
					'tx_atllocations_ajaxinterface[radius]': distance.value,
					'tx_atllocations_ajaxinterface[zip]': zip.value,
					'tx_atllocations_ajaxinterface[locationsToInclude]': locationUids,
					'tx_atllocations_ajaxinterface[includeDisposalLocations]': 1,
					'tx_atllocations_ajaxinterface[defaultZipSearchLocation]':
						props.defaultZipSearchLocation,
				},
				success: (data) => {
					isLoading.value = false;
					if (data.status !== 'error') {
						filteredLocations.value = data.locations;
						if (data.locations.length === 0) {
							errorMessage.value = translate(
								props.translations,
								'location.nothinginradius',
							);
						}
					} else {
						if (data.status === 'error' && data.code === '404') {
							errorMessage.value = translate(
								props.translations,
								'location.zipnotfound',
							);
						}
					}
				},
			});
		};

		const resetFilter = () => {
			filterActive.value = false;
			zip.value = '';
			distance.value = 10;
			filteredLocations.value = [];
			errorMessage.value = '';
			const event = new CustomEvent('reset-map-features');
			window.dispatchEvent(event);
		};

		watchEffect(() => {
			if (filterActive.value) {
				const event = new CustomEvent('update-map-features', {
					detail: toRaw(filteredLocations.value),
				});
				window.dispatchEvent(event);
			}
		});

		return {
			zip,
			distance,
			filteredLocations,
			defaultLocations,
			errorMessage,
			getFilteredLocations,
			resetFilter,
			isLoading,
			translate,
		};
	},
};
</script>
